import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
	createTheme,
	ThemeProvider,
	StyledEngineProvider,
	adaptV4Theme
} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { CardContent, Grid, Button, Checkbox, FormGroup, FormControlLabel } from '@mui/material';

import Tooltip from '@mui/material/Tooltip';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { ErrorMsg } from '../..';

import InputAdornment from '@mui/material/InputAdornment';

import Slider from '@mui/material/Slider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import { CardActions, Box, Chip } from '@mui/material';

import Typography from '@mui/material/Typography';

import { Loading } from '../..';
import { useUserState } from '../../../hook/customerHook';
import { getAllUsersAssignedToTask, getStatusByOrganization } from '../../../api/serverRequests';
import {
	priceOptions,
	numOptions,
	frontageOptions,
	areaOptions,
	subOptions,
	sizeOptions,
	yearOptions,
	highwayOptions,
	equityBuiltOptions,
	ftnProximity,
	skytrainStationProximity,
	phoneOptions,
	directionOptions,
	bedroomOptions,
	bathroomOptions,
	kitchenOptions,
	dwellingTypes
} from './formOptions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

const useStyles = makeStyles((theme) => ({
	form: {
		width: '100%', // Fix IE 11 issue.
		display: 'flex',
		flexFlow: 'column nowrap',
		'& .MuiList-root': {
			overflowX: 'auto',
			scrollbarWidth: 'none',
			gap: '0.2rem'
		},
		'& .MuiList-root::-webkit-scrollbar': {
			display: 'none'
		},
		'& .MuiListItem-root': {
			padding: theme.spacing(0, 1, 0, 0),
			width: 'auto'
		},
		'& .MuiChip-root': {
			padding: theme.spacing(2, 1),
			transition: 'none'
			// margin: theme.spacing(0, 1, 1, 0)
		},
		'& .MuiChip-clickable:active': {
			boxShadow: 'none'
		}
	},
	assessmentPriceSelector: {
		borderBottom: '1px solid lightgray',
		marginBottom: '1rem'
	},
	sellingStatusFilter: {
		gap: '0.7rem',
		marginTop: '1rem',
		paddingBottom: '1.5rem',
		marginBottom: '0.5rem',
		borderBottom: '1px solid lightgray',
		width: '100%'
	},
	submit: {
		margin: theme.spacing(0),
		backgroundColor: '#004F2B',
		boxShadow: 'none'
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff'
	},
	formControlLabel: {
		fontWeight: 'bold'
	},
	searchTypes: {
		marginBottom: theme.spacing(2),
		borderBottom: '1px solid lightgray'
	},
	searchTypesOptions: {
		marginLeft: theme.spacing(1),
		marginTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		width: '100%'
	},
	slider: {
		'& .MuiSlider-thumb': {
			width: 18,
			height: 18,
			backgroundColor: 'grey'
		},
		'& .MuiSlider-rail': {
			height: 4,
			backgroundColor: 'grey'
		},
		'& .MuiSlider-track': {
			height: 4,
			backgroundColor: 'grey'
		},
		'&:hover': {
			'& .MuiSlider-thumb': {
				backgroundColor: 'green'
			},
			'& .MuiSlider-track': {
				backgroundColor: 'green'
			},
			'& .MuiSlider-rail': {
				height: 4,
				backgroundColor: 'green'
			}
		},
		'& .MuiSlider-valueLabel': {
			left: 'unset'
		},
		'& .MuiSlider-mark': {
			display: 'none'
		}
	},
	separator: {
		padding: theme.spacing(2),
		userSelect: 'none',
		opacity: 0.5
	},
	content: {
		padding: theme.spacing(4),
		paddingTop: theme.spacing(2),
		paddingBottom: '0 !important',
		overflowY: 'auto',
		overflowX: 'hidden'
	},
	actions: {
		padding: theme.spacing(2, 4),
		backgroundColor: '#f8f8f8',
		borderTop: '1px solid rgba(0, 0, 0, 0.12)'
	}
}));

const theme = createTheme(
	adaptV4Theme({
		palette: {
			primary: {
				main: '#004F2B'
			}
		}
	})
);

function PriceSelector({ min, max, onChange, value }) {
	const defaultMin = Math.floor(max * 0.5 - max * 0.1);
	const defaultMax = Math.ceil(max * 0.5 + max * 0.1);

	function handleChange(val) {
		if (val[0] < val[1]) {
			onChange && onChange(val);
		}
	}

	const startAdornment = <InputAdornment position="start">$</InputAdornment>;
	return (
		<Grid
			container
			item
			xs={12}
			style={{ gap: '0.5rem', paddingBottom: '1.5rem', paddingTop: '2.5rem' }}
		>
			<PriceSlider value={value} min={min} max={max} onChange={(val) => handleChange(val)} />
		</Grid>
	);
}

function generateMarks() {
	const result = [];
	let index = 0;
	let scaledValue = 200_000;

	while (scaledValue <= 10_000_000) {
		result.push({ value: index, scaledValue });

		scaledValue += scaledValue < 1_000_000 ? 50_000 : 500_000;
		index += 1;
	}

	if (result && result[0]) {
		result[0]['label'] = '200K';
	}
	if (result && result[34]) {
		result[34]['label'] = '10M';
	}
	return result;
}

const priceMarkers = generateMarks();

const scale = (value) => {
	const mark = priceMarkers.find((el) => {
		return el.value === value;
	});

	if (!mark) {
		return 0;
	}
	return mark.scaledValue;
};

const unscale = (value) => {
	const mark = priceMarkers.find((el) => {
		return el.scaledValue === value;
	});

	if (!mark) {
		return 0;
	}
	return mark.value;
};

function valueLabelFormat(value) {
	const units = ['', 'K', 'M'];

	let unitIndex = 0;
	let scaledValue = value;

	while (scaledValue >= 1000 && unitIndex < units.length - 1) {
		unitIndex += 1;
		scaledValue /= 1000;
	}

	return `${scaledValue}${units[unitIndex]}`;
}

function PriceSlider({ value, min, max, onChange }) {
	const classes = useStyles();
	const [displayLabel, setDisplayLabel] = useState(value !== null ? 'on' : 'off');
	const defaultMin = Math.floor(max * 0.5 - max * 0.1);
	const defaultMax = Math.ceil(max * 0.5 + max * 0.1);
	function handleChange(e, val) {
		const newMin = scale(val[0]);
		const newMax = scale(val[1]);
		onChange([newMin, newMax]);
		setDisplayLabel('on');
	}

	return (
		<Slider
			className={classes.slider}
			defaultValue={[defaultMin, defaultMax]}
			value={value !== null ? value.map((el) => unscale(el)) : [defaultMin, defaultMax]}
			min={0}
			max={priceMarkers.length - 1}
			step={1}
			marks={priceMarkers}
			onChange={handleChange}
			valueLabelDisplay={value !== null ? 'on' : 'off'}
			aria-labelledby="range-slider"
			scale={scale}
			valueLabelFormat={(val) => (displayLabel === 'on' ? valueLabelFormat(val) : '')}
		/>
	);
}

function MinMaxTextFields({
	label,
	minLabel,
	maxLabel,
	minPlaceholder,
	maxPlaceholder,
	minProps,
	maxProps,

	value,
	min,
	max,
	type,

	disabled,

	onChange
}) {
	const classes = useStyles();

	return (
		<React.Fragment>
			<Typography style={{ color: 'black' }}>{label}</Typography>
			<Grid item xs={12}>
				<Box
					display="flex"
					flexDirection="row"
					flexWrap="nowrap"
					flexGrow={1}
					alignItems="center"
				>
					<TextField
						id="standard-multiline-flexible"
						label={minLabel}
						type={type || 'number'}
						variant="outlined"
						value={value !== null ? value[0] : ''}
						defaultValue={value !== null ? value[0] : ''}
						placeholder={minPlaceholder}
						InputLabelProps={{ shrink: true }}
						fullWidth
						disabled={disabled}
						// min={min}
						// max={value !== null ? value[1] : ''}
						InputProps={Object.assign(
							{},
							{ inputProps: { min, max: value !== null ? value[1] : '' } },
							minProps
						)}
						onChange={(e) => {
							const newMin = parseInt(e.target.value, 10);
							onChange([newMin, value ? value[1] : null]);
						}}
					/>
					<Typography className={classes.separator}>-</Typography>
					<TextField
						id="standard-multiline-flexible"
						label={maxLabel}
						type={type || 'number'}
						variant="outlined"
						value={value !== null ? value[1] : ''}
						min={value !== null ? value[0] : ''}
						max={max}
						fullWidth
						disabled={disabled}
						defaultValue={value !== null ? value[1] : ''}
						placeholder={maxPlaceholder}
						InputLabelProps={{ shrink: true }}
						InputProps={Object.assign(
							{},
							{ inputProps: { min: value !== null ? value[0] : '', max } },
							minProps
						)}
						onChange={(e) => {
							const newMax = parseInt(e.target.value, 10);
							onChange([value ? value[0] : null, newMax]);
						}}
					/>
				</Box>
			</Grid>
		</React.Fragment>
	);
}

function SelectChips({ label, options, value, onChange }) {
	const chips = options.map((option, index) => {
		const selected = value === option.value;

		return (
			<ListItem key={index}>
				<Chip
					variant={selected ? 'default' : 'outlined'}
					size="large"
					label={option.label}
					clickable={option.clickable}
					style={option.clickable ? {} : { opacity: 0.5, cursor: 'not-allowed' }}
					color={selected ? 'primary' : 'default'}
					onClick={() => {
						if (option.clickable && onChange) {
							onChange(selected ? null : option.value);
						}
					}}
				/>
			</ListItem>
		);
	});

	return (
		<React.Fragment>
			<Typography style={{ color: 'black' }} variant="subtitle1">
				{label}
			</Typography>
			<List style={{ display: 'flex', flexDirection: 'row' }}>{chips}</List>
		</React.Fragment>
	);
}

export default function SearchForm({
	handleSearch,
	handleRefreshSearch,
	handleClose,
	areaOptionsQuery,
	subAreaOptionsQuery,
	setRestoredFilters,
	restoredFilters,
	refreshListings
}) {
	const [userState, dispatchUser] = useUserState();

	const classes = useStyles();

	let defaultValuesEmpty = {
		// searchTypePriceDrop: false,
		// searchTypeBelowLastSold: false,
		// searchTypeBelowLastAssessment: false,

		dwellingTypes: 'detached',
		nonMlsSearch: false,
		mlsSearch: true,

		minMaxPrice: null,
		minMaxAssessment: null,
		bedrooms: null,
		bathrooms: null,
		kitchens: null,

		minMaxSqft: null,
		minMaxLotSize: null,
		minMaxYear: null,
		minMaxFrontage: null,

		highwayProximity: 'any',
		skytrainStationProximity: 'any',
		ftnProximity: 'any',
		equityBuiltOptions: 'any',
		direction: 'any',
		phoneNumber: 'any',

		tenant: false,
		underConstruction: false,
		hasFlatLot: false,
		forclosedProperties: false,
		laneAccess: false,
		cornerlotProperty: false,
		forSale: false,
		forSaleStartDate: null,
		soldStatus: false,
		soldStatusStartDate: null,
		soldStatusEndDate: null,

		expiredStatus: false,
		terminatedStatus: false,
		cancelProtectedStatus: false,
		expiredStatusStartDate: null,
		expiredStatusEndDate: null,

		includeCancelProtected: false,
		includePrivacyProtected: false,
		build: false
	};

	const defaultValues = Object.assign(
		{},
		defaultValuesEmpty,
		restoredFilters,
		userState.advFilters
	);

	const { handleSubmit, control, reset, watch, setValue } = useForm({
		defaultValues
	});

	const [errorMsg] = useState('');
	const [disabled, setDisabled] = useState(false);

	const terminatedStatus = watch('terminatedStatus');
	const expiredStatus = watch('expiredStatus');
	const cancelProtectedStatus = watch('cancelProtectedStatus');
	const forSale = watch('forSale');
	const soldStatus = watch('soldStatus');

	const handleReset = () => {
		reset(defaultValuesEmpty);
		if (restoredFilters) {
			for (const key of Object.keys(defaultValuesEmpty)) {
				if (key in restoredFilters) {
					delete restoredFilters[key];
				}
			}
		}
	};

	useEffect(() => {
		if (!terminatedStatus && !expiredStatus && !cancelProtectedStatus) {
			setDisabled(true);
			setValue('expiredStatusStartDate', null);
			setValue('expiredStatusEndDate', null);
		} else {
			setDisabled(false);
		}
	}, [terminatedStatus, expiredStatus, cancelProtectedStatus]);

	useEffect(() => {
		if (!forSale) {
			setValue('forSaleStartDate', null);
		}
	}, [forSale]);

	useEffect(() => {
		if (!soldStatus) {
			setValue('soldStatusStartDate', null);
			setValue('soldStatusEndDate', null);
		}
	}, [soldStatus]);

	const handleSearchSubmit = (formData) => {
		let filtersPersisted = {};

		// First, preserve any existing area, subarea, and map filters from restoredFilters
		if (restoredFilters) {
			// Preserve area and subarea filters
			if (restoredFilters.area) {
				filtersPersisted.area = restoredFilters.area;
			}
			if (restoredFilters.subarea) {
				filtersPersisted.subarea = restoredFilters.subarea;
			}
			// Preserve map filters
			if (restoredFilters.rectFilter) {
				filtersPersisted.rectFilter = restoredFilters.rectFilter;
			}
			if (restoredFilters.circleFilter) {
				filtersPersisted.circleFilter = restoredFilters.circleFilter;
			}
		}

		for (const key of Object.keys(formData)) {
			if (
				(formData[key] && formData[key] !== 'any') ||
				(key === 'detached' && formData['detached'] === false) ||
				(key === 'mlsSearch' && formData['mlsSearch'] === false)
			) {
				filtersPersisted[key] = formData[key];
				if (restoredFilters) restoredFilters[key] = formData[key];
			} else if (formData[key] && (formData[key] === 'any' || formData[key] === 'null')) {
				if (restoredFilters && key in restoredFilters) delete restoredFilters[key];
			}
		}
		dispatchUser({ advFilters: filtersPersisted });
		refreshListings(filtersPersisted);
		handleClose();
	};

	const isAnyStatusSelected = (watch) => {
		return (
			watch('soldStatus') ||
			watch('terminatedStatus') ||
			watch('expiredStatus') ||
			watch('cancelProtectedStatus')
		);
	};

	return (
		<React.Fragment>
			<form
				className={classes.form}
				id="advFilters"
				onSubmit={handleSubmit(handleSearchSubmit)}
			>
				<CardContent className={classes.content}>
					{errorMsg && <ErrorMsg text={errorMsg} />}
					<Grid container justifyContent="start" alignItems="center" spacing={2}>
						{/*<Grid item xs={12} className={classes.searchTypes}>
						<Typography variant="subtitle1" className={classes.formControlLabel}>
							Search type
						</Typography>
						<FormControl
							component="fieldset"
							className={classes.searchTypesOptions}
						>
							<FormGroup>
															<Controller
								name={'searchTypePriceDrop'}
								control={control}
								render={({ field }) => {
									return (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													color={'primary'}
													onChange={(e) => {
														field.onChange(e.target.checked);
													}}
												/>
											}
											label="Price drop from previous listing"
										/>
									);
								}}
							></Controller>
															<Controller
								name={'searchTypeBelowLastSold'}
								control={control}
								render={({ field }) => {
									return (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													color={'primary'}
													onChange={(e) => {
														field.onChange(e.target.checked);
													}}
												/>
											}
											label="Price below last sold"
										/>
									);
								}}
							></Controller>
															<Controller
								name={'searchTypeBelowLastAssessment'}
								control={control}
								render={({ field }) => {
									return (
										<FormControlLabel
											value="test1"
											control={
												<Checkbox
													checked={field.value}
													color={'primary'}
													onChange={(e) => {
														field.onChange(e.target.checked);
													}}
												/>
											}
											label="Price below assessment"
										/>
									);
								}}
							></Controller>
							</FormGroup>
						</FormControl>
					</Grid>*/}
						<Grid item xs={12} className={classes.assessmentPriceSelector}>
							<Grid item xs={6}>
								<Grid item xs={6}>
									<Typography>Dwelling type</Typography>
								</Grid>
							</Grid>

							<Grid item xs={12}>
								<FormControl
									component="fieldset"
									className={classes.searchTypesOptions}
								>
									<FormGroup style={{ flexDirection: 'row' }}>
										<Controller
											name={'dwellingTypes'}
											control={control}
											color="primary"
											render={({ field }) => {
												return (
													<React.Fragment>
														<SelectChips
															options={dwellingTypes.map(
																(option) => ({
																	...option,
																	clickable:
																		option.value !== 'acreage'
																})
															)}
															value={field.value}
															onChange={(data) => {
																if (data !== 'acreage') {
																	// Prevent selection of acreage
																	field.onChange(data);
																}
															}}
														/>
													</React.Fragment>
												);
											}}
										></Controller>
									</FormGroup>
								</FormControl>
							</Grid>
						</Grid>
						<Grid item xs={12} className={classes.assessmentPriceSelector}>
							<Typography className={classes.formControlLabel}>
								Property Source [beta]
							</Typography>
							<Grid item xs={12} container>
								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'mlsSearch'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="mlsSearch"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="MLS"
														/>
													);
												}}
											></Controller>
										</FormGroup>
									</FormControl>
								</Grid>
								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'nonMlsSearch'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="nonMlsSearch"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Never been on MLS"
														/>
													);
												}}
											></Controller>
										</FormGroup>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name={'minMaxPrice'}
								control={control}
								render={({ field }) => {
									return (
										<React.Fragment>
											<Typography className={classes.formControlLabel}>
												Price range
											</Typography>
											<PriceSelector
												onChange={(data) => {
													field.onChange(data);
												}}
												value={field.value}
												min={0}
												max={priceMarkers.length}
											/>
										</React.Fragment>
									);
								}}
							></Controller>
						</Grid>

						<Grid item xs={12} className={classes.assessmentPriceSelector}>
							<Controller
								name={'minMaxAssessment'}
								control={control}
								render={({ field }) => {
									return (
										<React.Fragment>
											<Typography className={classes.formControlLabel}>
												Assessment price range
											</Typography>
											<PriceSelector
												onChange={(data) => {
													field.onChange(data);
												}}
												value={field.value}
												min={0}
												max={priceMarkers.length}
											/>
										</React.Fragment>
									);
								}}
							></Controller>
						</Grid>
						<Grid className={classes.sellingStatusFilter}>
							<Typography variant="h5" component="h5" className={classes.subHeading}>
								Selling status
							</Typography>
							<FormControl
								component="fieldset"
								className={classes.searchTypesOptions}
							>
								<FormGroup>
									<Grid container direction="row" alignItems="center">
										<Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
											<Controller
												name={'forSale'}
												control={control}
												render={({ field }) => (
													<FormControlLabel
														control={
															<Checkbox
																name="forSale"
																checked={field.value}
																disabled={
																	watch('soldStatus') ||
																	watch('terminatedStatus') ||
																	watch('expiredStatus') ||
																	watch('cancelProtectedStatus')
																}
																color={'primary'}
																onChange={(e) => {
																	field.onChange(
																		e.target.checked
																	);
																	if (e.target.checked) {
																		setValue(
																			'soldStatus',
																			false
																		);
																		setValue(
																			'terminatedStatus',
																			false
																		);
																		setValue(
																			'expiredStatus',
																			false
																		);
																		setValue(
																			'cancelProtectedStatus',
																			false
																		);
																	}
																}}
															/>
														}
														label="For Sale"
													/>
												)}
											/>
										</Grid>
										<Grid item>
											<Grid
												container
												spacing={8}
												style={{ marginTop: '0px' }}
											>
												<Grid item>
													<Controller
														name={'forSaleStartDate'}
														control={control}
														render={({ field }) => (
															<TextField
																id="forSaleStartDate"
																label="Start Date"
																type="date"
																value={field.value}
																onChange={(e) => {
																	field.onChange(e.target.value);
																}}
																InputLabelProps={{
																	shrink: true
																}}
																disabled={!watch('forSale')}
																fullWidth
															/>
														)}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
									<Grid container direction="row" alignItems="center">
										<Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
											<Controller
												name={'soldStatus'}
												control={control}
												render={({ field }) => (
													<FormControlLabel
														control={
															<Checkbox
																name="soldStatus"
																checked={field.value}
																disabled={
																	watch('forSale') ||
																	watch('terminatedStatus') ||
																	watch('expiredStatus') ||
																	watch('cancelProtectedStatus')
																}
																color={'primary'}
																onChange={(e) => {
																	field.onChange(
																		e.target.checked
																	);
																	if (e.target.checked) {
																		setValue('forSale', false);
																		setValue(
																			'terminatedStatus',
																			false
																		);
																		setValue(
																			'expiredStatus',
																			false
																		);
																		setValue(
																			'cancelProtectedStatus',
																			false
																		);
																	}
																}}
															/>
														}
														label="Closed"
													/>
												)}
											/>
										</Grid>
										<Grid item>
											<Grid
												container
												spacing={8}
												style={{ marginTop: '0px' }}
											>
												<Grid item xs={6} sm={6}>
													<Controller
														name={'soldStatusStartDate'}
														control={control}
														render={({ field }) => (
															<TextField
																id="soldStatusStartDate"
																label="Start Date"
																type="date"
																value={field.value}
																onChange={(e) => {
																	field.onChange(e.target.value);
																}}
																InputLabelProps={{
																	shrink: true
																}}
																disabled={!watch('soldStatus')}
																fullWidth
															/>
														)}
													/>
												</Grid>
												<Grid item xs={6} sm={6}>
													<Controller
														name={'soldStatusEndDate'}
														control={control}
														render={({ field }) => (
															<TextField
																id="soldStatusEndDate"
																label="End Date"
																type="date"
																value={field.value}
																onChange={(e) => {
																	field.onChange(e.target.value);
																}}
																InputLabelProps={{
																	shrink: true
																}}
																disabled={!watch('soldStatus')}
																fullWidth
															/>
														)}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Grid>

									<Grid container direction="row" style={{ marginTop: '22px' }}>
										<Grid
											item
											container
											direction="column"
											xs={12}
											sm={4}
											md={4}
											lg={4}
											xl={4}
										>
											<Grid item>
												<Controller
													name={'terminatedStatus'}
													control={control}
													render={({ field }) => {
														return (
															<FormControlLabel
																control={
																	<Checkbox
																		name="terminatedStatus"
																		checked={field.value}
																		disabled={
																			watch('forSale') ||
																			watch('soldStatus')
																		}
																		color={'primary'}
																		onChange={(e) => {
																			field.onChange(
																				e.target.checked
																			);
																			if (e.target.checked) {
																				setValue(
																					'forSale',
																					false
																				);
																				setValue(
																					'soldStatus',
																					false
																				);
																			}
																		}}
																	/>
																}
																label="Terminated"
															/>
														);
													}}
												/>
											</Grid>
											<Grid item>
												<Controller
													name={'expiredStatus'}
													control={control}
													render={({ field }) => {
														return (
															<FormControlLabel
																style={{ marginRight: 'auto' }}
																control={
																	<Checkbox
																		name="expiredStatus"
																		checked={field.value}
																		disabled={
																			watch('forSale') ||
																			watch('soldStatus')
																		}
																		color={'primary'}
																		onChange={(e) => {
																			field.onChange(
																				e.target.checked
																			);
																			if (e.target.checked) {
																				setValue(
																					'forSale',
																					false
																				);
																				setValue(
																					'soldStatus',
																					false
																				);
																			}
																		}}
																	/>
																}
																label="Expired"
															/>
														);
													}}
												/>
											</Grid>
											<Grid item>
												<Controller
													name={'cancelProtectedStatus'}
													control={control}
													render={({ field }) => {
														return (
															<FormControlLabel
																control={
																	<Checkbox
																		name="cancelProtectedStatus"
																		checked={field.value}
																		disabled={
																			watch('forSale') ||
																			watch('soldStatus')
																		}
																		color={'primary'}
																		onChange={(e) => {
																			field.onChange(
																				e.target.checked
																			);
																			if (e.target.checked) {
																				setValue(
																					'forSale',
																					false
																				);
																				setValue(
																					'soldStatus',
																					false
																				);
																			}
																		}}
																	/>
																}
																label="Cancel protected"
																style={{ whiteSpace: 'nowrap' }}
															/>
														);
													}}
												/>
											</Grid>
										</Grid>
										<Grid item>
											<Grid
												container
												spacing={8}
												style={{ marginTop: '0px' }}
											>
												<Grid item xs={6} sm={6}>
													<Controller
														name={'expiredStatusStartDate'}
														control={control}
														render={({ field }) => (
															<TextField
																id="expiredStatusStartDate"
																label="Start Date"
																type="date"
																value={field.value}
																className={classes.textField}
																onChange={(e) => {
																	field.onChange(e.target.value);
																}}
																InputLabelProps={{
																	shrink: true
																}}
																disabled={disabled}
															/>
														)}
													/>
												</Grid>
												<Grid item xs={6} sm={6}>
													<Controller
														name={'expiredStatusEndDate'}
														control={control}
														render={({ field }) => (
															<TextField
																id="expiredStatusEndDate"
																label="End Date"
																type="date"
																value={field.value}
																className={classes.textField}
																onChange={(e) => {
																	field.onChange(e.target.value);
																}}
																InputLabelProps={{
																	shrink: true
																}}
																disabled={disabled}
															/>
														)}
													/>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</FormGroup>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<Typography
								variant="subtitle1"
								paragraph
								className={classes.formControlLabel}
							>
								Rooms
							</Typography>

							<Controller
								name={'bedrooms'}
								control={control}
								color="primary"
								render={({ field }) => {
									return (
										<React.Fragment>
											<SelectChips
												label={'Bedrooms'}
												options={bedroomOptions.map((option) => ({
													...option,
													clickable: true
												}))}
												value={field.value}
												onChange={(data) => {
													field.onChange(data);
												}}
											/>
										</React.Fragment>
									);
								}}
							></Controller>

							<Controller
								name={'bathrooms'}
								control={control}
								color="primary"
								render={({ field }) => {
									return (
										<React.Fragment>
											<SelectChips
												label={'Bathrooms'}
												options={bathroomOptions.map((option) => ({
													...option,
													clickable: true
												}))}
												value={field.value}
												onChange={(data) => {
													field.onChange(data);
												}}
											/>
										</React.Fragment>
									);
								}}
							></Controller>

							<Controller
								name={'kitchens'}
								control={control}
								color="primary"
								render={({ field }) => {
									return (
										<SelectChips
											label={'Kitchens'}
											options={kitchenOptions.map((option) => ({
												...option,
												clickable: true
											}))}
											value={field.value}
											onChange={(data) => {
												field.onChange(data);
											}}
										/>
									);
								}}
							></Controller>
						</Grid>
						<Grid container style={{ gap: '0.7rem' }} item xs={12} direction="column">
							<Typography variant="subtitle1" className={classes.formControlLabel}>
								Property details
							</Typography>

							<Controller
								name={'minMaxSqft'}
								control={control}
								render={({ field }) => {
									return (
										<MinMaxTextFields
											label="Total square feet"
											minLabel="min sqft"
											minPlaceholder="100"
											maxLabel="max sqft"
											maxPlaceholder="10,000"
											min={100}
											max={100_000}
											minProps={{
												endAdornment: (
													<InputAdornment position="end">
														sqft
													</InputAdornment>
												)
											}}
											value={field.value}
											onChange={(value) => {
												field.onChange(value);
											}}
										/>
									);
								}}
							></Controller>

							<Controller
								name={'minMaxLotSize'}
								control={control}
								color="primary"
								render={({ field }) => {
									return (
										<MinMaxTextFields
											label="Lot size"
											minLabel="min sqft"
											minPlaceholder="100"
											maxLabel="max sqft"
											maxPlaceholder="10,000"
											min={100}
											max={100_000}
											minProps={{
												endAdornment: (
													<InputAdornment position="end">
														sqft
													</InputAdornment>
												)
											}}
											value={field.value}
											onChange={(value) => {
												field.onChange(value);
											}}
										/>
									);
								}}
							></Controller>

							<Controller
								name={'minMaxFrontage'}
								control={control}
								color="primary"
								render={({ field }) => {
									return (
										<MinMaxTextFields
											label="Frontage"
											minLabel="min ft"
											minPlaceholder="20"
											maxLabel="max ft"
											maxPlaceholder="1000"
											min={0}
											max={100000}
											minProps={{
												endAdornment: (
													<InputAdornment position="end">
														ft
													</InputAdornment>
												)
											}}
											value={field.value}
											onChange={(value) => {
												field.onChange(value);
											}}
										/>
									);
								}}
							></Controller>
							<Controller
								name={'minMaxYear'}
								control={control}
								color="primary"
								render={({ field }) => {
									const min = 1950;
									const max = new Date().getFullYear();

									return (
										<MinMaxTextFields
											label="Year built"
											minLabel="min year"
											minPlaceholder={min.toString()}
											maxLabel="max year"
											maxPlaceholder={max.toString()}
											min={min}
											max={max}
											value={field.value}
											onChange={(value) => {
												field.onChange(value);
											}}
										/>
									);
								}}
							></Controller>

							<Grid
								item
								container
								style={{
									gap: '0.7rem',
									marginTop: '1rem',
									paddingBottom: '1.5rem',
									marginBottom: '0.5rem',
									borderBottom: '1px solid lightgray'
								}}
							>
								{/* <Grid item container xs={12} style={{ alignItems: 'center' }}>
									<Grid item xs={6}>
										<Typography>Highway Proximity</Typography>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name={'highwayProximity'}
											control={control}
											color="primary"
											render={({ onChange, value }) => {
												return (
													<Select
														fullWidth
														value={value}
														onChange={(value) => {
															onChange(value);
														}}
														style={{
															color:
																value === 'any' ? 'gray' : 'black'
														}}
													>
														{highwayOptions.map((option) => {
															if (option.value !== 'any') {
																return (
																	<MenuItem value={option.value}>
																		{option.label}
																	</MenuItem>
																);
															} else {
																return (
																	<MenuItem
																		value="any"
																		style={{
																			color: 'gray'
																		}}
																	>
																		{option.label}
																	</MenuItem>
																);
															}
														})}
													</Select>
												);
											}}
										></Controller>
									</Grid>
								</Grid> */}
								{/* <Grid item container xs={12} style={{ alignItems: 'center' }}>
									<Grid item xs={6}>
										<Typography>Direction</Typography>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name={'direction'}
											control={control}
											color="primary"
											render={({ onChange, value }) => {
												return (
													<Select
														fullWidth
														value={value}
														onChange={(value) => {
															onChange(value);
														}}
														style={{
															color:
																value === 'any' ? 'gray' : 'black'
														}}
													>
														{directionOptions.map((option) => {
															if (option.value !== 'any') {
																return (
																	<MenuItem value={option.value}>
																		{option.label}
																	</MenuItem>
																);
															} else {
																return (
																	<MenuItem
																		value="any"
																		style={{
																			color: 'gray'
																		}}
																	>
																		{option.label}
																	</MenuItem>
																);
															}
														})}
													</Select>
												);
											}}
										></Controller>
									</Grid>
								</Grid> */}
								{/* <Grid item container xs={12} style={{ alignItems: 'center' }}>
									<Grid item xs={6}>
										<Typography>Phone Number</Typography>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name={'phoneNumber'}
											control={control}
											color="primary"
											render={({ onChange, value }) => {
												return (
													<Select
														fullWidth
														value={value}
														onChange={(value) => {
															onChange(value);
														}}
														style={{
															color:
																value === 'any' ? 'gray' : 'black'
														}}
													>
														{phoneOptions.map((option) => {
															if (option.value !== 'any') {
																return (
																	<MenuItem value={option.value}>
																		{option.label}
																	</MenuItem>
																);
															} else {
																return (
																	<MenuItem
																		value="any"
																		style={{
																			color: 'gray'
																		}}
																	>
																		{option.label}
																	</MenuItem>
																);
															}
														})}
													</Select>
												);
											}}
										></Controller>
									</Grid>
								</Grid> */}
							</Grid>
						</Grid>
						<Grid item xs={12} className={classes.assessmentPriceSelector}>
							<Typography className={classes.formControlLabel}>
								Property Condition
							</Typography>
							<Grid item xs={12} container>
								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'tenant'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="occupancy"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Include Tenanted"
														/>
													);
												}}
											></Controller>
										</FormGroup>
									</FormControl>
								</Grid>

								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'underConstruction'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="underConstruction"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Include Under Construction"
														/>
													);
												}}
											></Controller>
											<Controller
												name={'landAssembly'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="build"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Include Land Assembly"
														/>
													);
												}}
											></Controller>
										</FormGroup>
									</FormControl>
								</Grid>
							</Grid>
							<Typography className={classes.formControlLabel}>
								Privacy Options
							</Typography>
							<Grid item xs={12} container>
								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'includePrivacyProtected'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="includePrivacyProtected"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Include Privacy Protected"
														/>
													);
												}}
											></Controller>
										</FormGroup>
									</FormControl>
								</Grid>

								<Grid item xs={6}>
									<FormControl
										component="fieldset"
										className={classes.searchTypesOptions}
									>
										<FormGroup>
											<Controller
												name={'includeCancelProtected'}
												control={control}
												render={({ field }) => {
													return (
														<FormControlLabel
															control={
																<Checkbox
																	name="includeCancelProtected"
																	checked={field.value}
																	color={'primary'}
																	onChange={(e) => {
																		field.onChange(
																			e.target.checked
																		);
																	}}
																/>
															}
															label="Include Cancel Protected"
														/>
													);
												}}
											></Controller>{' '}
										</FormGroup>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Typography className={classes.formControlLabel}>
								Development Options
							</Typography>

							<FormControl
								component="fieldset"
								className={classes.searchTypesOptions}
							>
								<FormGroup>
									{/* <Controller
										name={'build'}
										control={control}
										render={({ onChange, value }) => {
											return (
												<FormControlLabel
													control={
														<Checkbox
															name="build"
															checked={value}
															color={'primary'}
															onChange={(e) => {
																onChange(e.target.checked);
															}}
														/>
													}
													label="Likely to Build"
												/>
											);
										}}
									></Controller>{' '} */}
								</FormGroup>
							</FormControl>
							<Grid item container xs={12} style={{ alignItems: 'center' }}>
								<Grid item xs={6}>
									<Typography>Equity built</Typography>
								</Grid>
								<Grid item xs={6}>
									<Controller
										name={'equityBuiltOptions'}
										control={control}
										color="primary"
										render={({ field }) => {
											return (
												<Select
													fullWidth
													value={field.value}
													onChange={(value) => {
														field.onChange(value);
													}}
													style={{
														color:
															field.value === 'any' ? 'gray' : 'black'
													}}
												>
													{equityBuiltOptions.map((option) => {
														if (option.value !== 'any') {
															return (
																<MenuItem value={option.value}>
																	{option.label}
																</MenuItem>
															);
														} else {
															return (
																<MenuItem
																	value="any"
																	style={{
																		color: 'gray'
																	}}
																>
																	{option.label}
																</MenuItem>
															);
														}
													})}
												</Select>
											);
										}}
									></Controller>
								</Grid>
							</Grid>
							<Grid item container xs={12} style={{ alignItems: 'center' }}>
								<Tooltip title="Frequent-Transit Network">
									<Grid item xs={6}>
										<Typography>
											<a
												target="_blank"
												href="https://www.translink.ca/-/media/translink/documents/plans-and-projects/frequent-transit-network/frequent_transit_network_map.pdf"
											>
												FTN
											</a>{' '}
											Area Proximity
										</Typography>
									</Grid>
								</Tooltip>
								<Grid item xs={6}>
									<Controller
										name={'ftnProximity'}
										control={control}
										color="primary"
										render={({ field }) => {
											return (
												<Select
													fullWidth
													value={field.value}
													onChange={(value) => {
														field.onChange(value);
													}}
													style={{
														color:
															field.value === 'any' ? 'gray' : 'black'
													}}
												>
													{ftnProximity.map((option) => {
														if (option.value !== 'any') {
															return (
																<MenuItem value={option.value}>
																	{option.label}
																</MenuItem>
															);
														} else {
															return (
																<MenuItem
																	value="any"
																	style={{
																		color: 'gray'
																	}}
																>
																	{option.label}
																</MenuItem>
															);
														}
													})}
												</Select>
											);
										}}
									></Controller>
								</Grid>
							</Grid>
							<Grid item container xs={12} style={{ alignItems: 'center' }}>
								<Tooltip title="Transit-Oriented Development">
									<Grid item xs={6}>
										<Typography>
											<a target="_blank" href="/tod_locations_map.html">
												TOD
											</a>{' '}
											Area Proximity
										</Typography>
									</Grid>
								</Tooltip>
								<Grid item xs={6}>
									<Controller
										name={'skytrainStationProximity'}
										control={control}
										color="primary"
										render={({ field }) => {
											return (
												<Select
													fullWidth
													multiple
													value={
														Array.isArray(field.value)
															? field.value
															: []
													}
													onChange={(event) => {
														const selectedValues = event.target.value;
														const filteredValues =
															selectedValues.filter(
																(val) => val !== 'any'
															);
														field.onChange(filteredValues);
													}}
													renderValue={(selected) => (
														<div
															style={{
																display: 'flex',
																flexWrap: 'wrap'
															}}
														>
															{selected.map((val) => (
																<Chip
																	key={val}
																	label={
																		skytrainStationProximity.find(
																			(option) =>
																				option.value === val
																		)?.label
																	}
																	style={{ margin: 2 }}
																/>
															))}
														</div>
													)}
													style={{
														color:
															field.value &&
															field.value.includes('any')
																? 'gray'
																: 'black'
													}}
												>
													{skytrainStationProximity.map((option) => {
														if (option.value !== 'any') {
															return (
																<MenuItem
																	key={option.value}
																	value={option.value}
																	style={
																		option.value === 'any'
																			? { color: 'gray' }
																			: {}
																	}
																>
																	{option.label}
																</MenuItem>
															);
														} else {
															return (
																<MenuItem
																	value="any"
																	style={{
																		color: 'gray'
																	}}
																>
																	{option.label}
																</MenuItem>
															);
														}
													})}
												</Select>
											);
										}}
									></Controller>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Typography className={classes.formControlLabel}>
								Additional property info
							</Typography>

							<FormControl
								component="fieldset"
								className={classes.searchTypesOptions}
							>
								<FormGroup>
									<Controller
										name={'forclosedProperties'}
										control={control}
										render={({ field }) => {
											return (
												<FormControlLabel
													control={
														<Checkbox
															name="forclosedProperties"
															checked={field.value}
															color={'primary'}
															onChange={(e) => {
																field.onChange(e.target.checked);
															}}
														/>
													}
													label="Foreclosed"
												/>
											);
										}}
									></Controller>
								</FormGroup>
								<FormGroup>
									<Controller
										name={'lanewayAccess'}
										control={control}
										render={({ field }) => {
											return (
												<FormControlLabel
													control={
														<Checkbox
															name="lanewayAccess"
															checked={field.value}
															color={'primary'}
															onChange={(e) => {
																field.onChange(e.target.checked);
															}}
														/>
													}
													label="Lane Access"
												/>
											);
										}}
									></Controller>
								</FormGroup>
								<FormGroup>
									<Controller
										name={'cornerlotProperty'}
										control={control}
										render={({ field }) => {
											return (
												<FormControlLabel
													control={
														<Checkbox
															name="cornerlotProperty"
															checked={field.value}
															color={'primary'}
															onChange={(e) => {
																field.onChange(e.target.checked);
															}}
														/>
													}
													label="Corner Lot"
												/>
											);
										}}
									></Controller>
								</FormGroup>
								<FormGroup>
									{/* <Controller
										name={'hasFlatLot'}
										control={control}
										render={({ onChange, value }) => {
											return (
												<FormControlLabel
													control={
														<Checkbox
															name="hasFlatLot"
															checked={value}
															color={'primary'}
															onChange={(e) => {
																onChange(e.target.checked);
															}}
														/>
													}
													label="Flat Lot"
												/>
											);
										}}
									></Controller> */}
								</FormGroup>
							</FormControl>
						</Grid>
					</Grid>
				</CardContent>
				<CardActions className={classes.actions}>
					<Grid item container alignItems="center">
						<Grid item xs={6}>
							<Button
								onClick={() => {
									handleReset();
								}}
							>
								Clear All
							</Button>
						</Grid>
						<Grid item xs={6}>
							<Button
								// type="submit"
								onClick={handleSubmit(handleSearchSubmit)}
								id="advFilters"
								fullWidth
								size="large"
								variant="contained"
								color="primary"
								className={classes.submit}
							>
								Apply
							</Button>
						</Grid>
					</Grid>
				</CardActions>
			</form>
		</React.Fragment>
	);
}
